// src/components/ErrorBoundary.js

import React from 'react';
import { Box, Typography, Button, Paper, Collapse } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import logger from '../../utils/logger';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, showDetails: false, copied: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Hatayı logla (logger.error production'da da console'a yazar)
        logger.error('React Error Boundary yakaladı:', error, errorInfo);

        // State'i güncelle
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Burada Sentry, LogRocket gibi servislere gönderilebilir
        // if (process.env.NODE_ENV === 'production') {
        //   Sentry.captureException(error);
        // }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false, copied: false });
        window.location.href = '/';
    };

    // Hata mesajı + bileşen yığınını metin olarak döndürür (kopyalama / gösterim için)
    getDetailsText = () => {
        const { error, errorInfo } = this.state;
        const message = error?.toString() || 'Bilinmeyen hata';
        const stack = errorInfo?.componentStack || '';
        return `${message}\n${stack}`.trim();
    };

    handleCopyDetails = async () => {
        try {
            await navigator.clipboard.writeText(this.getDetailsText());
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        } catch (e) {
            // Pano erişimi yoksa sessizce geç — kullanıcı detayı yine de görebiliyor
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        backgroundColor: '#f5f5f5',
                        padding: 3
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            maxWidth: 600,
                            padding: 4,
                            textAlign: 'center'
                        }}
                    >
                        <ErrorOutlineIcon
                            sx={{
                                fontSize: 80,
                                color: 'error.main',
                                marginBottom: 2
                            }}
                        />
                        <Typography variant="h4" gutterBottom color="error">
                            Bir Hata Oluştu
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Üzgünüz, beklenmeyen bir hata oluştu. Bu sorunu mümkün olan en kısa sürede çözmek için çalışıyoruz.
                        </Typography>

                        {/* Teknik detaylar — production dahil her ortamda erişilebilir.
                            Varsayılan kapalı; kullanıcı isterse açıp destek için kopyalayabilir. */}
                        {this.state.error && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
                                    endIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                transition: 'transform 0.2s',
                                                transform: this.state.showDetails ? 'rotate(180deg)' : 'none'
                                            }}
                                        />
                                    }
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Teknik detaylar
                                </Button>

                                <Collapse in={this.state.showDetails}>
                                    <Box sx={{
                                        mt: 1,
                                        p: 2,
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                        textAlign: 'left',
                                        overflowX: 'auto',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {this.getDetailsText()}
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<ContentCopyIcon fontSize="small" />}
                                            onClick={this.handleCopyDetails}
                                            sx={{ mt: 1, textTransform: 'none' }}
                                        >
                                            {this.state.copied ? 'Kopyalandı' : 'Detayı kopyala'}
                                        </Button>
                                    </Box>
                                </Collapse>
                            </Box>
                        )}

                        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={this.handleReset}
                            >
                                Ana Sayfaya Dön
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => window.location.reload()}
                            >
                                Sayfayı Yenile
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
